// src/components/common/ReportDom.js
import React, { forwardRef } from "react";
import InstitutionProfile from "./InstitutionProfile";
import moment from "moment";
import { numberFormat, toCompanyDateOnlyforReportDateFilter } from "../../lib/functions";
import "./print.css";

const ReportDom = forwardRef(
  (
    {
      title,
      filters,
      dateLabel,      // optional preformatted period line; overrides fromDate/toDate
      columns = [],   // ✅ default empty array
      data = [],      // ✅ default empty array
      extraSections = [],
      style,
      renderTable,
      mode,
      showTotal = true,
        orientation = "portrait"

    },
    ref
  ) => {
    // Compute totals for numeric columns
    const totals = {};
    if (data.length > 0) {
      columns.forEach((col) => {
        if (col.dataIndex && typeof data[0][col.dataIndex] === "number") {
          totals[col.dataIndex] = data.reduce(
            (acc, row) => acc + (row[col.dataIndex] || 0),
            0
          );
        }
      });
    }

    const printableColumns = columns.filter(
      (col) => col.title?.toLowerCase() !== "actions"
    );

    return (
      <div className="page-break" ref={ref} style={style}>
        {/* Header with logo & institution info */}
        {/* ================= HEADER ================= */}
        <div className="report-header">
          {mode !== "receipt-voucher" && mode !== "no-title" && <InstitutionProfile />}

          {/* Report title */}
          {title && mode !== "no-title" && (<div className="report-title">
            <h2>
              {title || "Report"}
            </h2>
          </div>)}

          {/* Filter info */}
          {dateLabel && mode !== "no-title" && (
            <p className="report-filters">
              <strong>Period:</strong> {dateLabel}
            </p>
          )}
          {!dateLabel && (filters?.fromDate || filters?.toDate) && mode !== "no-title" && (
            <p className="report-filters">
              <strong>Date Range:</strong>{" "}
              {filters?.fromDate && toCompanyDateOnlyforReportDateFilter(filters.fromDate).format("DD-MM-YYYY")}
              {filters?.fromDate && filters?.toDate && " — "}
              {filters?.toDate && toCompanyDateOnlyforReportDateFilter(filters.toDate).format("DD-MM-YYYY")}
            </p>
          )}


        </div>

        {/* Pure HTML Table */}
        <div className="report-body">
          {renderTable ? renderTable() : <table className="print-table">
            <thead>
              <tr>
                {printableColumns.map((col) => (
                  <th key={col.key || col.dataIndex}>{col.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  {printableColumns.map((col) => (
                    <td key={col.key || col.dataIndex}>
                      {col.render
                        ? col.render(row[col.dataIndex], row, i)
                        : row[col.dataIndex]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>

            {/* Totals Row */}
            {Object.keys(totals).length > 0 && (
              <tfoot>
                {showTotal !== false && (
                  <tr>
                    <td colSpan={printableColumns.length - 1}>
                      <strong>Grand Total</strong>
                    </td>
                    <td>
                      {Object.values(totals).map((t, i) => (
                        <div key={i}>{numberFormat(Number(t).toFixed(2))}</div>
                      ))}
                    </td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>}


          {/* Extra sections */}
          {extraSections.map((section, idx) => (
            <div key={idx} style={{ marginTop: "24px" }}>
              <h4>{section.title}</h4>
              <table className="print-table">
                <thead>
                  <tr>
                    {section.columns.map((col) => (
                      <th key={col.key || col.dataIndex}>{col.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.data.map((row, i) => (
                    <tr key={i}>
                      {section.columns.map((col) => (
                        <td key={col.key || col.dataIndex}>
                          {col.render
                            ? col.render(row[col.dataIndex], row, i)
                            : row[col.dataIndex]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {/* ================= FOOTER ================= */}
        <div className="report-footer">
          {mode !== "receipt-voucher" && mode !== "report-page" && mode !== "no-title" && mode !== "stampOn" && mode !== "signatureOn" &&
            <div className="footer-left">
              <p>Notes</p>
              <p>Thanks for your business.</p>
            </div>}

          {mode !== "receipt-voucher" && mode !== "report-page" && mode !== "no-title" && mode !== "signatureOn" && <div className="footer-right">
            {filters?.fromDate && <p>Generated on {moment().format("DD-MM-YYYY HH:mm")}</p>}
            {!filters?.fromDate && <div className="signature-block">
              <p className="signature">Signature</p>
              <div className="signature-line" />
            </div>}
          </div>}
        </div>
      </div>
    );
  }
);

export default ReportDom;
